import React, { useState, useEffect } from "react";
import {
  PlusIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { SlideshowLightbox } from "lightbox.js-react";

const TaskonDashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [taskImages, setTaskImages] = useState({});
  const [selectedTaskForGallery, setSelectedTaskForGallery] = useState(null);
  const [selectedTaskImages, setSelectedTaskImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [showGalleryLightbox, setShowGalleryLightbox] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const [formData, setFormData] = useState({
    site: "",
    customer_id: null,
    task: "",
    description: "",
    date_from: "",
    date_to: "",
    status: "undone",
    image: null,
  });

  // Load tasks from API or localStorage
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/tasks`);
        setTasks(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.warn(
          "Failed to fetch tasks from API, using localStorage:",
          err.message,
        );
        // Fallback to localStorage
        const savedTasks = localStorage.getItem("tasks");
        const parsedTasks = savedTasks ? JSON.parse(savedTasks) : [];

        // If no tasks exist, initialize with sample tasks
        if (parsedTasks.length === 0) {
          const sampleTasks = [];
          setTasks(sampleTasks);
          localStorage.setItem("tasks", JSON.stringify(sampleTasks));
        } else {
          setTasks(parsedTasks);
        }
      }
    };

    fetchTasks();
  }, []);

  // Sync tasks to localStorage whenever tasks change
  useEffect(() => {
    localStorage.setItem("tasks", JSON.stringify(tasks));
  }, [tasks]);

  // Fetch customers from API
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/customers`);
        setCustomers(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.warn("Failed to fetch customers from API:", err.message);
        setCustomers([]);
      }
    };

    fetchCustomers();
  }, []);

  // Fetch task images
  useEffect(() => {
    const fetchTaskImages = async () => {
      if (tasks.length === 0) return;

      try {
        const imagesCounts = {};

        // Initialize all tasks with 0 count
        tasks.forEach((task) => {
          imagesCounts[task.id] = 0;
        });

        // Fetch all task images at once
        const response = await axios.get(`${apiUrl}/api/task-img`);
        const allImages = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];

        // Group images by task_id and count them
        allImages.forEach((image) => {
          if (image.task_id) {
            imagesCounts[image.task_id] =
              (imagesCounts[image.task_id] || 0) + 1;
          }
        });

        setTaskImages(imagesCounts);
        console.log("Task images counts:", imagesCounts);
      } catch (err) {
        console.warn("Failed to fetch task images:", err.message);
      }
    };

    fetchTaskImages();
  }, [tasks, apiUrl]);

  // Auto-dismiss success message after 4 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Filter tasks based on selected status
  const getFilteredTasks = () => {
    if (filterStatus === 'all') return tasks;
    return tasks.filter(task => task.status === filterStatus);
  };

  // Get task statistics for filter badges
  const getTaskStats = () => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'done').length;
    const pending = tasks.filter(t => t.status === 'undone').length;
    return { total, completed, pending };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for site selection to capture customer_id
    if (name === "site") {
      const selectedCustomer = customers.find((c) => c.name === value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        customer_id: selectedCustomer ? selectedCustomer.id : null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.site || !formData.task) {
      alert("Please fill in all required fields (Site, Task)");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare FormData for file upload
      const formDataToSubmit = new FormData();
      formDataToSubmit.append("site", formData.site);
      formDataToSubmit.append("customer_id", formData.customer_id);
      formDataToSubmit.append("task", formData.task);
      formDataToSubmit.append("description", formData.description);
      formDataToSubmit.append("date_from", formData.date_from);
      formDataToSubmit.append("date_to", formData.date_to);
      formDataToSubmit.append("status", formData.status);

      // Add image if provided
      if (formData.image) {
        formDataToSubmit.append("image", formData.image);
      }

      console.log("Submitting to:", `${apiUrl}/api/tasks`);
      console.log(
        "Form data prepared with image:",
        formData.image ? formData.image.name : "No image",
      );

      const response = await axios.post(
        `${apiUrl}/api/tasks`,
        formDataToSubmit,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      console.log("Task created successfully:", response.data);
      setTasks((prev) => [...prev, response.data]);
      setSuccess("Task saved successfully!");

      // Reset form on success
      setFormData({
        site: "",
        customer_id: null,
        task: "",
        description: "",
        date_from: "",
        date_to: "",
        status: "undone",
        image: null,
      });
      setShowForm(false);
    } catch (err) {
      console.error("Error details:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: {
          url: err.config?.url,
          method: err.config?.method,
        },
      });

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save task. Please check your API connection and try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const formatCreatedDate = (task) => {
    // Check for various possible date field names from API
    const createdDate = task.created_date || task.createdAt || task.created_at;
    if (!createdDate) return "N/A";
    return new Date(createdDate).toLocaleDateString();
  };

  const openImageGallery = async (taskId) => {
    setSelectedTaskForGallery(taskId);
    setGalleryLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/task-img`);
      const allImages = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];
      console.log("All images from API:", allImages);

      // Filter images for the selected task
      const taskImagesList = allImages.filter(
        (image) => image.task_id === taskId,
      );
      console.log(
        `Found ${taskImagesList.length} images for task ${taskId}:`,
        taskImagesList,
      );

      // Add detailed logging for first image structure
      if (taskImagesList.length > 0) {
        console.log("First image structure:", taskImagesList[0]);
        console.log("Image field:", taskImagesList[0].image);
        console.log("Image_path field:", taskImagesList[0].image_path);
      }

      setSelectedTaskImages(taskImagesList);
    } catch (err) {
      console.error("Failed to fetch task images for gallery:", err.message);
      setError("Failed to load images. Please try again.");
    } finally {
      setGalleryLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If already a full URL, use it as-is

    if (apiUrl.startsWith("http://"))
      // Use /storage/ path with apiUrl
      return `${apiUrl}/storage/${imagePath}`;

    if (apiUrl.startsWith("https://"))
      // Use /storage/ path with apiUrl
      return `${apiUrl}/public/storage/${imagePath}`;
  };

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex justify-between items-center">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            ✕
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex justify-between items-center">
          <p className="text-green-800">{success}</p>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-600 hover:text-green-800"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Scheduled Tasks
            </h2>
          </div>
        </div>
      </div>

      {/* Add Task Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Add New Task
          </h3>
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site *
              </label>
              <select
                name="site"
                value={formData.site}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              >
                <option value="">Select a customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.name}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task *
              </label>
              <input
                type="text"
                name="task"
                value={formData.task}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g., Foundation work"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Detailed description of the task..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Image (Optional)
              </label>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {formData.image && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ {formData.image.name} selected
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date From
              </label>
              <input
                type="date"
                name="date_from"
                value={formData.date_from}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date To
              </label>
              <input
                type="date"
                name="date_to"
                value={formData.date_to}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="md:col-span-2 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#23A19B] text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-4 h-4" />
                    <span>Add Task</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tasks List with Filters */}
      <div className="space-y-6">
        {/* Filter Buttons */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700 mr-2"></span>
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-2 py-2 rounded-full text-sm font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Tasks
              {/* <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                {getTaskStats().total}
              </span> */}
            </button>
            <button
              onClick={() => setFilterStatus('undone')}
              className={`px-2 py-2 rounded-full text-sm font-medium transition-colors ${
                filterStatus === 'undone'
                  ? 'bg-orange-500 text-white'
                  : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
              }`}
            >
              Pending
              {/* <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-orange-200 text-orange-800">
                {getTaskStats().pending}
              </span> */}
            </button>
            <button
              onClick={() => setFilterStatus('done')}
              className={`px-2 py-2 rounded-full text-sm font-medium transition-colors ${
                filterStatus === 'done'
                  ? 'bg-green-500 text-white'
                  : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              Completed
              {/* <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-200 text-green-800">
                {getTaskStats().completed}
              </span> */}
            </button>
          </div>
        </div>

        {/* Tasks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {getFilteredTasks().length === 0 ? (
            <div className="md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filterStatus === 'all' 
                  ? 'No tasks yet' 
                  : filterStatus === 'done' 
                    ? 'No completed tasks' 
                    : 'No pending tasks'}
              </h3>
              <p className="text-gray-500">
                {filterStatus === 'all' 
                  ? 'Create your first task to get started' 
                  : filterStatus === 'done' 
                    ? 'Complete some tasks to see them here' 
                    : 'All tasks are completed! 🎉'}
              </p>
            </div>
          ) : (
            getFilteredTasks().map((task) => (
              <div
                key={task.id}
                className={`bg-white rounded-lg shadow-sm border-l-4 p-6 relative ${
                  task.status === "done"
                    ? "border-l-green-500 bg-green-50"
                    : "border-l-orange-500 bg-orange-50"
                }`}
              >
                {/* Task Content - No edit/delete buttons */}
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-semibold text-gray-500">
                      #{task.task_number}
                    </span>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        task.status === "done"
                          ? "bg-green-100 text-green-800"
                          : "bg-orange-100 text-orange-800"
                      }`}
                    >
                      {task.status === "done" ? "Completed" : "Pending"}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {task.task}
                  </h3>

                  <div className="space-y-1 text-sm text-gray-600 mb-3">
                    <p>
                      <span className="font-medium">Site:</span>{" "}
                      {task.customer?.name || "N/A"}
                    </p>
                    {task.description && (
                      <p>
                        <span className="font-medium">Description:</span>{" "}
                        {task.description}
                      </p>
                    )}
                  </div>

                  {task.image && (
                    <div className="mb-3">
                      <SlideshowLightbox>
                        <img
                          src={getImageUrl(task.image)}
                          alt="Task"
                          className="w-full aspect-video object-cover rounded-md border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setSelectedImage(getImageUrl(task.image))}
                          onError={(e) => {
                            console.warn(
                              "Failed to load image:",
                              getImageUrl(task.image),
                            );
                            e.target.style.display = "none";
                          }}
                        />
                      </SlideshowLightbox>
                    </div>
                  )}

                  <div className="space-y-1 text-sm text-gray-600 mb-3">
                    <div className="flex space-x-4">
                      <p>
                        <span className="font-medium">From:</span>{" "}
                        {formatDate(task.date_from)}
                      </p>
                      <p>
                        <span className="font-medium">To:</span>{" "}
                        {formatDate(task.date_to)}
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 flex items-center justify-between">
                    <span>Created: {formatCreatedDate(task)}</span>
                    {taskImages[task.id] > 0 && (
                      <button
                        onClick={() => openImageGallery(task.id)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                      >
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {taskImages[task.id]}{" "}
                        {taskImages[task.id] === 1 ? "image" : "images"}
                      </button>
                    )}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Single Image Preview Modal */}
      {selectedImage && !selectedTaskForGallery && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-screen overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Image Preview
              </h3>
              <button
                onClick={() => setSelectedImage(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="p-4 flex justify-center">
              <SlideshowLightbox>
                <img
                  src={selectedImage}
                  alt="Full Preview"
                  className="max-w-full max-h-[calc(100vh-200px)] object-contain"
                />
              </SlideshowLightbox>
            </div>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {selectedTaskForGallery !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setSelectedTaskForGallery(null);
            setSelectedTaskImages([]);
            setShowGalleryLightbox(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-6xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Task Images ({selectedTaskImages.length})
              </h3>
              <button
                onClick={() => {
                  setSelectedTaskForGallery(null);
                  setSelectedTaskImages([]);
                  setShowGalleryLightbox(false);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {galleryLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#23A19B]"></div>
                </div>
              ) : selectedTaskImages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No images found for this task</p>
                </div>
              ) : (
                <SlideshowLightbox
                  images={selectedTaskImages.map(image => ({
                    src: getImageUrl(image.image || image.image_path),
                    alt: image.image_path || 'Task Image'
                  }))}
                  open={showGalleryLightbox}
                  onClose={() => setShowGalleryLightbox(false)}
                  showThumbnails={true}
                  theme="light"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {selectedTaskImages.map((image, index) => (
                      <div
                        key={image.id || index}
                        className="relative bg-gray-100 rounded-lg overflow-hidden group cursor-pointer"
                        onClick={() => setShowGalleryLightbox(true)}
                      >
                        <img
                          src={getImageUrl(image.image || image.image_path)}
                          alt={`Task image ${index + 1}`}
                          className="w-full aspect-video object-cover group-hover:opacity-75 transition-opacity"
                          onError={(e) => {
                            console.warn(
                              "Failed to load image:",
                              image.image || image.image_path,
                            );
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </SlideshowLightbox>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskonDashboard;